import type { Descendant } from "slate";
import {
  AIFlowchartContent,
  AIFlowchartStep,
  AIUnifiedContent,
  AIUnifiedLevel,
  AIContentElement,
} from "../components/tool_components/text-editor/types/ai-templates";

// --- TYPE DEFINITIONS ---

// Input types (derived from 'embedded-template' JSON)
interface EmbeddedTemplateItem {
  color: string;
  title: string;
  iconId: string;
  description: string;
  icon_description: string;
}

interface EmbeddedTemplateLevel {
  items: EmbeddedTemplateItem[];
  level_title: string;
}

interface EmbeddedTemplateData {
  title: string;
  levels: EmbeddedTemplateLevel[];
  genericColors: string[];
}

interface EmbeddedTemplateElement {
  type: "embedded-template";
  children: [{ text: string }];
  template_data: EmbeddedTemplateData;
  selected_template_name: string;
}

// Helper function to check if a node is an embedded template element
function isEmbeddedTemplateElement(node: any): node is EmbeddedTemplateElement {
  return (
    node &&
    node.type === "embedded-template" &&
    typeof node.template_data === "object" &&
    typeof node.selected_template_name === "string"
  );
}

// --- TRANSFORMATION FUNCTION ---

export function transformEmbeddedTemplatesToAIContent(
  contentList: Descendant[],
): Descendant[] {
  console.log("coming is", contentList);
  return contentList.map((block) => {
    console.log("block is", block);
    if (
      isEmbeddedTemplateElement(block) &&
      block.selected_template_name === "flowchart"
    ) {
      console.log("block is flowchart");
      const { template_data } = block;

      if (
        !template_data.levels ||
        template_data.levels.length === 0 ||
        !template_data.levels[0].items
      ) {
        console.warn(
          `Skipping transformation for flowchart due to missing levels/items: ${
            template_data.title || "Untitled"
          }`,
        );
        return block; // Return original block if data is insufficient
      }

      const firstLevelItems: AIFlowchartStep[] =
        template_data.levels[0].items.map((item) => ({
          // Explicit mapping to ensure type compatibility and handle potential missing optional fields
          title: item.title,
          description: item.description,
          icon_description: item.icon_description,
          iconId: item.iconId,
          color: item.color,
        }));
      const numItems = firstLevelItems.length;

      const aiResultKey = `unified_1_${numItems}`;

      // The `template_data.levels` items are structurally compatible with `AIUnifiedItem`
      // and `template_data.levels` itself is compatible with `AIUnifiedLevel[]`
      const unifiedLevels: AIUnifiedLevel[] = template_data.levels.map(
        (level) => ({
          level_title: level.level_title,
          // Ensure all properties for AIUnifiedItem are present, even if undefined
          items: level.items.map((item) => ({
            title: item.title,
            description: item.description,
            icon_description: item.icon_description,
            iconId: item.iconId,
            color: item.color,
          })),
          // You might want to add icon_description, color, iconId to EmbeddedTemplateLevel
          // if they can exist there, otherwise they will be undefined here.
          // icon_description: level.icon_description,
          // color: level.color,
          // iconId: level.iconId,
        }),
      );

      const newAiContentBlock: AIContentElement = {
        type: "ai-content",
        aiResult: {
          template_contents: {
            [aiResultKey]: {
              title: template_data.title,
              levels: unifiedLevels, // Use the mapped levels
              genericColors: template_data.genericColors,
            } as AIUnifiedContent, // Cast to AIUnifiedContent
          },
        },
        children: block.children || [{ text: "" }],
        template_content: {
          title: template_data.title,
          steps: firstLevelItems,
          genericColors: template_data.genericColors,
        } as AIFlowchartContent, // Cast to AIFlowchartContent
        selected_template: `FlowchartTemplate_${numItems}`,
      };
      return newAiContentBlock as Descendant;
    } else {
      console.log("block is not flowchart");
    }
    // If not an "embedded-template" of type "flowchart", or if type guard fails,
    // return the block unchanged.
    return block;
  });
}
